import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, MessageCircle, Users, Menu, X, Clock } from 'lucide-react';
import { AdvisorAvatar } from '../components/AdvisorAvatar';
import { Sidebar } from '../components/Sidebar';
import { AdvisorSelection } from '../components/AdvisorSelection';
import { CreateAdvisorModal } from '../components/CreateAdvisorModal';
import { useAuth } from '../hooks/useAuth';
import { signOut, getAdvisors, createCustomAdvisor, getUserConversations } from '../lib/supabase';

const defaultAdvisors = [
  {
    id: '1',
    name: 'Beatrice',
    title: 'The Strategist',
    description: 'Strategic thinking and decision-making',
    isDefault: true
  },
  {
    id: '2',
    name: 'Samuel',
    title: 'The Mentor',
    description: 'Personal growth and leadership',
    isDefault: true
  },
  {
    id: '3',
    name: 'Leo',
    title: 'The Innovator',
    description: 'Creative solutions and innovation',
    isDefault: true
  },
  {
    id: '4',
    name: 'Marcus',
    title: 'The Stoic',
    description: 'Philosophy and mindset',
    isDefault: true
  },
  {
    id: '5',
    name: 'Dr. Taylor',
    title: 'The Analyst',
    description: 'Data-driven insights and analysis',
    isDefault: true
  }
];

const mockChats = [
  {
    id: '1',
    title: 'Career Pivot Strategy',
    participants: ['Beatrice', 'Samuel'],
    lastMessage: 'Consider your long-term goals...',
    timestamp: '2 hours ago',
    isGroup: true
  },
  {
    id: '2',
    title: 'Innovation Project',
    participants: ['Leo'],
    lastMessage: 'Think outside conventional frameworks...',
    timestamp: '1 day ago',
    isGroup: false
  },
  {
    id: '3',
    title: 'Leadership Challenge',
    participants: ['Samuel', 'Marcus', 'Beatrice'],
    lastMessage: 'Focus on what you can control...',
    timestamp: '3 days ago',
    isGroup: true
  }
];

export const Boardroom: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, loading, emailVerified, signOut } = useAuth();
  const [selectedAdvisors, setSelectedAdvisors] = useState<string[]>([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showCreateAdvisor, setShowCreateAdvisor] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isCreatingAdvisor, setIsCreatingAdvisor] = useState(false);
  const [advisors, setAdvisors] = useState<any[]>([]);
  const [isLoadingAdvisors, setIsLoadingAdvisors] = useState(true);
  const [conversations, setConversations] = useState<any[]>([]);
  const [newAdvisorData, setNewAdvisorData] = useState({
    name: '',
    expertise: '',
    description: '',
    gender: 'male' as 'male' | 'female'
  });

  // Redirect if not authenticated or email not verified
  React.useEffect(() => {
    if (!loading) {
      if (!user || !emailVerified) {
        navigate('/');
      } else if (!profile) {
        // User is authenticated but hasn't completed onboarding
        navigate('/onboarding');
      }
    }
  }, [loading, user, emailVerified, profile, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-orange-400/20 border-t-orange-400 animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your boardroom...</p>
        </div>
      </div>
    );
  }

  // Don't render if not properly authenticated
  if (!user || !emailVerified || !profile) {
    return null;
  }

  // Load advisors and conversations on mount
  React.useEffect(() => {
    if (user) {
      console.log('User authenticated, loading data...', { userId: user.id, email: user.email });
      loadAdvisors();
      loadConversations();
    }
  }, [user]);

  const loadAdvisors = async () => {
    console.log('Starting to load advisors...');
    setIsLoadingAdvisors(true);
    try {
      const { data } = await getAdvisors();
      console.log('Loaded advisors from database:', data?.length || 0);
      if (data) {
        const formattedAdvisors = data.map(advisor => ({
          id: advisor.id,
          name: advisor.name,
          title: advisor.title,
          description: advisor.description,
          isDefault: advisor.is_default
        }));
        console.log('Formatted advisors:', formattedAdvisors);
        setAdvisors(formattedAdvisors);
      } else {
        console.warn('No advisors found in database');
        // Set default advisors as fallback
        console.log('Using fallback advisors');
        setAdvisors(defaultAdvisors);
      }
    } catch (error) {
      console.error('Failed to load advisors:', error);
      // Use default advisors as fallback
      setAdvisors(defaultAdvisors);
      alert('Could not load advisors from database. Using default advisors.');
    } finally {
      setIsLoadingAdvisors(false);
    }
  };

  const loadConversations = async () => {
    console.log('Starting to load conversations...');
    if (!user) return;
    try {
      const { data } = await getUserConversations(user.id);
      console.log('Conversations loaded:', data?.length || 0);
      if (data) {
        setConversations(data.map(conv => ({
          id: conv.id,
          title: conv.title,
          participants: [], // Would need to map advisor_ids to names
          lastMessage: conv.messages?.[0]?.content || 'No messages yet',
          timestamp: new Date(conv.last_message_at).toLocaleDateString(),
          isGroup: conv.conversation_type === 'group'
        })));
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      // Continue without conversations
    }
  };

  const toggleAdvisorSelection = (advisorId: string) => {
    setSelectedAdvisors(prev => 
      prev.includes(advisorId)
        ? prev.filter(id => id !== advisorId)
        : [...prev, advisorId]
    );
  };

  const startChat = () => {
    if (selectedAdvisors.length > 0) {
      console.log('Starting chat with selected advisors:', selectedAdvisors);
      console.log('Available advisors:', advisors.map(a => ({ id: a.id, name: a.name })));
      
      // Validate that selected advisors exist in our advisor list
      const validAdvisors = selectedAdvisors.filter(id => 
        advisors.some(advisor => advisor.id === id)
      );
      
      if (validAdvisors.length === 0) {
        alert('Please select valid advisors before starting a chat.');
        return;
      }
      
      if (validAdvisors.length !== selectedAdvisors.length) {
        console.warn('Some selected advisors are invalid:', {
          selected: selectedAdvisors,
          valid: validAdvisors,
          available: advisors.map(a => a.id)
        });
      }
      
      const isGroup = selectedAdvisors.length > 1;
      const chatId = `chat_${Date.now()}`;
      const advisorParam = validAdvisors.join(',');
      const chatUrl = `/boardroom/${chatId}?type=${isGroup ? 'group' : 'individual'}&advisors=${advisorParam}`;
      
      console.log('Navigating to chat:', {
        chatId,
        type: isGroup ? 'group' : 'individual',
        advisors: validAdvisors,
        url: chatUrl
      });
      
      // Navigate with selected advisor IDs
      navigate(chatUrl);
    }
  };

  const openExistingChat = (chatId: string, isGroup: boolean) => {
    navigate(`/boardroom/${chatId}?type=${isGroup ? 'group' : 'individual'}`);
  };

  const handleCreateAdvisor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (isCreatingAdvisor) return;
    
    if (newAdvisorData.name && newAdvisorData.expertise && newAdvisorData.description) {
      setIsCreatingAdvisor(true);
      
      // Create comprehensive personality prompt
      const personalityPrompt = `You are ${newAdvisorData.name}, ${newAdvisorData.description}

PRIMARY ROLE: You are an expert advisor specializing in ${newAdvisorData.expertise}. Your role is to provide thoughtful, professional guidance to help users make better decisions and achieve their goals.

CORE PRINCIPLES:
• Always maintain a professional, respectful, and helpful tone
• Ask clarifying questions when the user's request is vague or could benefit from more context
• Provide specific, actionable advice rather than generic platitudes
• Draw upon your expertise in ${newAdvisorData.expertise} while staying within your area of competence
• Be honest about limitations - if something is outside your expertise, acknowledge it

COMMUNICATION STYLE:
• Keep responses conversational and engaging (2-3 sentences unless more detail is specifically requested)
• Use clear, accessible language - avoid unnecessary jargon
• Be encouraging while remaining realistic about challenges
• Show empathy and understanding for the user's situation

CONTENT GUIDELINES:
• Never use inappropriate, offensive, or unprofessional language
• Avoid giving medical, legal, or financial advice unless that's specifically your area of expertise
• Do not make promises about outcomes you cannot guarantee
• Focus on actionable insights and practical next steps
• Encourage critical thinking rather than providing simple answers to complex problems

CLARIFICATION STRATEGY:
When facing unclear requests, ask specific questions like:
• "What's your primary goal in this situation?"
• "What constraints or resources should I consider?"
• "What have you already tried?"
• "What does success look like to you?"

Remember: You're here to empower users with knowledge and perspective, not to make decisions for them. Guide them toward their own informed conclusions.`;

      const advisorData = {
        name: newAdvisorData.name,
        title: newAdvisorData.expertise,
        description: newAdvisorData.description,
        personality_prompt: personalityPrompt,
        gender: newAdvisorData.gender,
        is_default: false,
        created_by: user?.id
      };
      
      try {
        const { data, error } = await createCustomAdvisor(advisorData);
        if (!error && data) {
          await loadAdvisors();
          setNewAdvisorData({ name: '', expertise: '', description: '', gender: 'male' });
          setShowCreateAdvisor(false);
        } else {
          alert(error?.message || 'Failed to create advisor. Please try again.');
        }
      } catch (error) {
        console.error('Error creating advisor:', error);
        alert('Failed to create advisor. Please try again.');
      } finally {
        setIsCreatingAdvisor(false);
      }
    }
  };

  const handleSignOut = () => {
    signOut().then(() => {
      navigate('/');
    });
  };

  const handleNewChatClick = () => {
    setShowNewChat(true);
    setShowMobileSidebar(false);
  };

  const handleFormChange = (field: string, value: string) => {
    setNewAdvisorData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-white">Boardroom</h1>
          </div>
          <button
            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
            className="p-2 rounded-xl bg-white/10 text-white"
          >
            {showMobileSidebar ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <Sidebar
        user={user}
        profile={profile}
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        onNewChatClick={handleNewChatClick}
        chats={conversations}
        onChatClick={openExistingChat}
        onSignOut={handleSignOut}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-20 lg:pt-8">
        {/* Show Recent Chats First - More Prominent */}
        {!showNewChat && (
          <div className="p-4 lg:p-8 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Recent Conversations</h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNewChatClick}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Chat</span>
              </motion.button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {conversations.map((chat) => (
                <motion.div
                  key={chat.id}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => openExistingChat(chat.id, chat.isGroup)}
                  className="p-4 sm:p-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <MessageCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <h3 className="text-white font-medium truncate">{chat.title}</h3>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    {chat.participants.slice(0, 4).map((participant, index) => (
                      <AdvisorAvatar key={participant} name={participant} size="small" />
                    ))}
                    {chat.participants.length > 4 && (
                      <span className="text-white/60 text-xs">+{chat.participants.length - 4}</span>
                    )}
                    <span className="text-white/40 text-xs ml-auto">
                      {chat.isGroup ? 'Group Chat' : '1:1 Session'}
                    </span>
                  </div>
                  
                  <p className="text-white/60 text-sm mb-2 line-clamp-2">{chat.lastMessage}</p>
                  
                  <div className="flex items-center gap-1 text-white/40 text-xs">
                    <Clock className="w-3 h-3" />
                    {chat.timestamp}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Area - New Chat Selection */}
        <div className="flex-1 flex flex-col">
          {showNewChat ? (
            <div className="flex-1 flex flex-col">
              {isLoadingAdvisors ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full border-4 border-orange-400/20 border-t-orange-400 animate-spin mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading advisors...</p>
                  </div>
                </div>
              ) : advisors.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.084 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">No Advisors Available</h3>
                    <p className="text-white/70 mb-6">
                      We couldn't load the advisors. This might be a database connection issue.
                    </p>
                    <button
                      onClick={loadAdvisors}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : (
                <AdvisorSelection
                  advisors={advisors}
                  selectedAdvisors={selectedAdvisors}
                  onToggleAdvisor={toggleAdvisorSelection}
                  onCreateAdvisor={() => setShowCreateAdvisor(true)}
                  onStartChat={startChat}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 flex items-center justify-center mx-auto mb-6 sm:mb-8">
                  <MessageCircle className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Ready for Your Next Session?</h2>
                <p className="text-lg sm:text-xl text-white/70 mb-6 sm:mb-8 px-4">
                  Continue a conversation above or start fresh with your advisors
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNewChat(true)}
                  className="px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
                >
                  Start New Chat
                </motion.button>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Create Advisor Modal */}
      <CreateAdvisorModal
        show={showCreateAdvisor}
        onClose={() => setShowCreateAdvisor(false)}
        formData={newAdvisorData}
        onFormChange={handleFormChange}
        onSubmit={handleCreateAdvisor}
        isLoading={isCreatingAdvisor}
      />
    </div>
  );
};